import { useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import type { Workout, SelectedRoutine, User, WorkoutExercise, ExerciseCatalogEntry, TabType } from '../types';
import { clampNonNegativeNumber } from '../utils/dataSanitization';
import { getLocalDateString, parseLocalDate } from '../utils/dateUtils';
import { isDemoId } from '../data/mockData';
import { clearDraft } from './useWorkoutDraft';
import { OFFLINE_CACHE_KEYS, readCache, type LoadSource, writeCache } from '../utils/offlineCache';
import type { QueuedWorkout } from './useWorkoutQueue';

export type SaveState = 'idle' | 'saving' | 'saved';

function getWorkoutFinalSets(exercise: WorkoutExercise, exerciseIndex: number, setsToSave: Set<string>) {
  return exercise.sets.map((set, setIndex) => (
    setsToSave.has(`${exerciseIndex}-${setIndex}`)
      ? set
      : { weight: 0, reps: 0 }
  ));
}

export function buildQueuedWorkout(entry: QueuedWorkout): Workout {
  return {
    id: entry.id,
    client_id: entry.clientId,
    user_id: entry.userId,
    routine_id: entry.routine.routine_id,
    routine_name: entry.routine.name?.trim() || 'Entrenamiento',
    date: entry.routine.date || getLocalDateString(),
    training_plan_id: entry.routine.planContext?.planId ?? null,
    pending: true,
    syncStatus: entry.status,
    exercises: entry.routine.exercises.map((exercise, exerciseIndex) => ({
      exercise_id: exercise.exercise_id,
      name: exercise.name,
      sets: getWorkoutFinalSets(exercise, exerciseIndex, new Set(entry.confirmedSets)),
    })),
  };
}

export function mergeQueuedWorkouts(baseWorkouts: Workout[], queue: QueuedWorkout[]): Workout[] {
  const pendingWorkouts = queue.map(buildQueuedWorkout);
  const filteredBase = baseWorkouts.filter((workout) => !workout.pending);
  return [...filteredBase, ...pendingWorkouts].sort((a, b) => b.date.localeCompare(a.date));
}

export function useWorkouts(
  user: User | null,
  showNotification: (msg: string, type: 'success' | 'error') => void,
  showConfirm: (msg: string, onConfirm: () => void, onCancel?: () => void) => void,
  ensureExerciseEntry: (name: string) => Promise<ExerciseCatalogEntry>,
  setActiveTab: (tab: TabType) => void,
  setSelectedWorkoutDetail: (workout: any) => void,
  enqueue: (routine: SelectedRoutine, sets: Set<string>) => Promise<QueuedWorkout>,
  queue: QueuedWorkout[]
) {
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [workoutsLoading, setWorkoutsLoading] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState<SelectedRoutine | null>(null);
  const selectedRoutineRef = useRef<SelectedRoutine | null>(null);
  selectedRoutineRef.current = selectedRoutine;
  const [confirmedSets, setConfirmedSets] = useState<Set<string>>(new Set());
  const confirmedSetsRef = useRef<Set<string>>(confirmedSets);
  confirmedSetsRef.current = confirmedSets;
  const [activeExerciseIndex, setActiveExerciseIndex] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const visibleWorkouts = mergeQueuedWorkouts(workouts, queue);

  const loadWorkouts = useCallback(async (): Promise<LoadSource> => {
    if (!user) return 'empty';
    setWorkoutsLoading(true);
    try {
      const { data, error } = await supabase
        .from('workouts')
        .select(`
          id,
          user_id,
          routine_id,
          routine_name,
          routine_name_snapshot,
          date,
          training_plan_id,
          workout_exercises (
            id,
            exercise_id,
            order_index,
            exercise_name_snapshot,
            workout_sets ( id, set_index, weight, reps )
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const mappedWorkouts: Workout[] = (data || []).map((workout: any) => ({
        id: workout.id,
        user_id: workout.user_id,
        routine_id: workout.routine_id != null ? String(workout.routine_id) : undefined,
        routine_name: workout.routine_name_snapshot || workout.routine_name || 'Entrenamiento',
        date: workout.date,
        training_plan_id: workout.training_plan_id ?? null,
        exercises: (workout.workout_exercises || [])
          .slice()
          .sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0))
          .map((ex: any) => ({
            exercise_id: ex.exercise_id,
            name: ex.exercise_name_snapshot || 'Ejercicio',
            sets: (ex.workout_sets || [])
              .slice()
              .sort((a: any, b: any) => (a.set_index ?? 0) - (b.set_index ?? 0))
              .map((set: any) => ({
                weight: set.weight ?? 0,
                reps: set.reps ?? 0,
              })),
          })),
      }));

      setWorkouts(mappedWorkouts);
      await writeCache(OFFLINE_CACHE_KEYS.workouts(user.id), mappedWorkouts, user.id);
      return 'network';
    } catch (error) {
      console.error('Error cargando entrenamientos:', error);
      const cached = await readCache<Workout[]>(OFFLINE_CACHE_KEYS.workouts(user.id), user.id);
      if (cached) {
        setWorkouts(cached);
        return 'cache';
      }
      return 'empty';
    } finally {
      setWorkoutsLoading(false);
    }
  }, [user]);

  const getLastSetsForExercise = (exerciseId?: string, exerciseName?: string) => {
    const sortedWorkouts = [...visibleWorkouts].sort((a, b) => b.date.localeCompare(a.date));
    for (const workout of sortedWorkouts) {
      for (const exercise of workout.exercises) {
        const idMatch = exerciseId && exercise.exercise_id === exerciseId;
        const nameMatch = !idMatch && exerciseName && 
          exercise.name.toLowerCase().trim() === exerciseName.toLowerCase().trim();
        
        if (idMatch || nameMatch) {
          const hasData = exercise.sets.some(s => s.weight > 0 || s.reps > 0);
          if (hasData) return exercise.sets;
        }
      }
    }
    return null;
  };

  const openWorkoutForEditing = (workout: Workout) => {
    const performedSets = new Set<string>();
    workout.exercises.forEach((ex, ei) =>
      ex.sets.forEach((set, si) => {
        if ((set.weight ?? 0) > 0 || (set.reps ?? 0) > 0) {
          performedSets.add(`${ei}-${si}`);
        }
      })
    );
    setConfirmedSets(performedSets);
    setActiveExerciseIndex(0);
    setSaveState('idle');
    setSelectedRoutine({
      ...workout,
      isEditing: true,
      isDemo: false,
      name: workout.routine_name,
      routine_id: workout.routine_id,
      exercises: workout.exercises.map(ex => ({ ...ex, sets: ex.sets.map(s => ({ ...s })) })),
    });
    setSelectedWorkoutDetail(null);
    setActiveTab('workout');
  };

  const startWorkout = (routine: any, planContext?: any) => {
    const isDemo = isDemoId(routine.id);

    if (!isDemo) {
      const today = getLocalDateString();
      const existing = routine.id
        ? visibleWorkouts.find(w => w.date === today && w.routine_id === routine.id)
        : null;

      if (existing) {
        showConfirm(
          `Ya entrenaste "${routine.name}" hoy. ¿Querés editar ese entrenamiento?`,
          () => openWorkoutForEditing(existing)
        );
        return;
      }
    }

    setConfirmedSets(new Set());
    setActiveExerciseIndex(0);
    setSaveState('idle');
    setSelectedRoutine({
      ...routine,
      routine_id: routine.id,
      date: getLocalDateString(),
      isDemo: isDemo,
      planContext: planContext ?? null,
      exercises: routine.exercises.map((ex: any) => {
        const lastSets = getLastSetsForExercise(ex.exercise_id, ex.name);
        return {
          exercise_id: ex.exercise_id,
          name: ex.name,
          sets: Array.from({ length: ex.sets }, (_, i) =>
            lastSets && i < lastSets.length
              ? { weight: lastSets[i].weight, reps: lastSets[i].reps }
              : { weight: 0, reps: 0 }
          ),
        };
      }),
    });
    setActiveTab('workout');
  };

  const updateWorkoutSet = (exerciseIndex: number, setIndex: number, field: string, value: string) => {
    // Functional update: sequential calls in one event (e.g. filling weight and
    // reps together) must not clobber each other via a stale closure.
    setSelectedRoutine((prev) => {
      if (!prev) return prev;
      const newExercises = prev.exercises.map((ex, ei) =>
        ei !== exerciseIndex ? ex : {
          ...ex,
          sets: ex.sets.map((s, si) =>
            si !== setIndex ? s : { ...s, [field]: clampNonNegativeNumber(value) }
          ),
        }
      );
      return { ...prev, exercises: newExercises };
    });
  };

  const addWorkoutSet = (exerciseIndex: number) => {
    if (!selectedRoutine) return;
    const newExercises = selectedRoutine.exercises.map((ex, ei) => {
      if (ei !== exerciseIndex) return ex;
      const last = ex.sets[ex.sets.length - 1];
      const seed = last ? { weight: last.weight, reps: last.reps } : { weight: 0, reps: 0 };
      return { ...ex, sets: [...ex.sets, seed] };
    });
    setSelectedRoutine({ ...selectedRoutine, exercises: newExercises });
  };

  const removeWorkoutSet = (exerciseIndex: number, setIndex: number) => {
    if (!selectedRoutine) return;
    const targetExercise = selectedRoutine.exercises[exerciseIndex];
    if (!targetExercise || targetExercise.sets.length <= 1) return;

    const newExercises = selectedRoutine.exercises.map((ex, ei) =>
      ei !== exerciseIndex ? ex : {
        ...ex,
        sets: ex.sets.filter((_, si) => si !== setIndex),
      }
    );
    setSelectedRoutine({ ...selectedRoutine, exercises: newExercises });

    // Renumber confirmedSets keys for the affected exercise: drop the deleted
    // index and shift down any keys with set index > setIndex.
    setConfirmedSets((prev) => {
      const next = new Set<string>();
      prev.forEach((key) => {
        const [exStr, siStr] = key.split('-');
        const ex = Number(exStr);
        const si = Number(siStr);
        if (ex !== exerciseIndex) {
          next.add(key);
          return;
        }
        if (si === setIndex) return;
        if (si > setIndex) {
          next.add(`${ex}-${si - 1}`);
        } else {
          next.add(key);
        }
      });
      return next;
    });
  };

  const persistWorkout = useCallback(async (
    routine: SelectedRoutine,
    setsToSave: Set<string>,
    onSuccess?: () => Promise<void>,
    clientId?: string
  ) => {
    if (!user) return;
    const workoutDate = routine.date || getLocalDateString();

    const routineName = routine.name?.trim() || 'Entrenamiento';
    const workoutPayload: Record<string, any> = {
      user_id: user.id,
      routine_id: routine.routine_id ?? null,
      routine_name: routineName,
      routine_name_snapshot: routineName,
      date: workoutDate,
    };
    if (clientId) {
      workoutPayload.client_id = clientId;
    }

    // Only set training_plan_id for new workouts or when planContext is explicitly provided.
    // When editing, the existing value is preserved by omitting the field from the update.
    if (!routine.isEditing || routine.planContext) {
      workoutPayload.training_plan_id = routine.planContext?.planId ?? null;
    }

    let workoutId = routine.id;
    if (routine.isEditing && workoutId) {
      const { error } = await supabase.from('workouts').update(workoutPayload).eq('id', workoutId);
      if (error) throw error;
    } else {
      const workoutQuery = clientId
        ? supabase
            .from('workouts')
            .upsert(workoutPayload, { onConflict: 'user_id,client_id' })
            .select('id')
            .single()
        : supabase.from('workouts').insert(workoutPayload).select('id').single();
      const { data, error } = await workoutQuery;
      if (error) throw error;
      workoutId = data.id;
    }

    // Queued workouts sync in the background: they must not touch the session
    // currently open, or finishing it would overwrite the synced workout.
    if (!routine.isEditing && !clientId) {
      setSelectedRoutine((prev) =>
        prev ? { ...prev, id: workoutId, isEditing: true } : prev
      );
    }

    const { error: deleteError } = await supabase.from('workout_exercises').delete().eq('workout_id', workoutId);
    if (deleteError) throw deleteError;

    const resolvedExercises: WorkoutExercise[] = [];
    for (const [exIndex, ex] of routine.exercises.entries()) {
      const trimmedName = ex.name?.trim();
      if (!trimmedName) continue;
      let exerciseId = ex.exercise_id;
      if (!exerciseId) {
        const catalogEntry = await ensureExerciseEntry(trimmedName);
        exerciseId = catalogEntry.id;
      }
      const finalSets = getWorkoutFinalSets(ex, exIndex, setsToSave);
      resolvedExercises.push({ ...ex, exercise_id: exerciseId, name: trimmedName, sets: finalSets });
    }

    const { data: exerciseRows, error: exError } = await supabase
      .from('workout_exercises')
      .insert(resolvedExercises.map((ex, index) => ({
        workout_id: workoutId,
        exercise_id: ex.exercise_id,
        exercise_name_snapshot: ex.name,
        order_index: index,
      })))
      .select('id, order_index');

    if (exError) throw exError;

    const setsPayload = (exerciseRows || []).flatMap(row => {
      const ex = resolvedExercises[row.order_index];
      return ex.sets.map((set, setIndex) => ({
        workout_exercise_id: row.id,
        set_index: setIndex,
        weight: set.weight ?? 0,
        reps: set.reps ?? 0,
      }));
    });

    if (setsPayload.length > 0) {
      const { error: setError } = await supabase.from('workout_sets').insert(setsPayload);
      if (setError) throw setError;
    }

    if (onSuccess) {
      await onSuccess();
    }
  }, [ensureExerciseEntry, user]);

  const persistQueuedWorkout = useCallback(async (entry: QueuedWorkout): Promise<void> => {
    const routine: SelectedRoutine = {
      ...entry.routine,
      isEditing: false,
    };
    await persistWorkout(routine, new Set(entry.confirmedSets), undefined, entry.clientId);
  }, [persistWorkout]);

  const finishAndClose = (fromCalendar?: boolean) => {
    if (fromCalendar) setActiveTab('progress');
    setSelectedRoutine(null);
    setConfirmedSets(new Set());
    setActiveExerciseIndex(0);
    setSaveState('idle');
    if (user) clearDraft(user.id);
  };

  const saveWorkout = async (onSuccess: () => Promise<void>) => {
    if (!selectedRoutine || !user || saveState === 'saving') return;

    const totalSets = selectedRoutine.exercises.reduce((sum, ex) => sum + ex.sets.length, 0);
    let completedSets = 0;
    selectedRoutine.exercises.forEach((ex, ei) => {
      ex.sets.forEach((set, si) => {
        if (confirmedSets.has(`${ei}-${si}`) && (set.reps ?? 0) > 0) {
          completedSets++;
        }
      });
    });
    const allComplete = totalSets > 0 && completedSets >= totalSets;

    if (!allComplete) {
      const unchecked = totalSets - completedSets;
      showConfirm(
        `${unchecked} serie${unchecked > 1 ? 's' : ''} sin completar se guardará${unchecked > 1 ? 'n' : ''} como no realizada${unchecked > 1 ? 's' : ''}. ¿Terminar?`,
        async () => {
          const currentRoutine = selectedRoutineRef.current;
          const currentConfirmed = confirmedSetsRef.current;
          if (!currentRoutine) return;
          if (!navigator.onLine) {
            if (currentRoutine.isEditing) {
              showNotification('No se puede editar sin conexión', 'error');
              setSaveState('idle');
              return;
            }

            const missingCatalog = currentRoutine.exercises.some((exercise) => !exercise.exercise_id);
            if (missingCatalog) {
              showNotification('Conectate para guardar', 'error');
              setSaveState('idle');
              return;
            }

            try {
              await enqueue(currentRoutine, currentConfirmed);
              finishAndClose(currentRoutine.fromCalendar);
              showNotification('Guardado sin conexión. Se sincroniza al reconectar.', 'success');
            } catch (error) {
              console.error('Error guardando entrenamiento offline:', error);
              showNotification('Error al guardar el entrenamiento offline', 'error');
            } finally {
              setSaveState('idle');
            }
            return;
          }
          setSaveState('saving');
          try {
            await persistWorkout(currentRoutine, currentConfirmed, onSuccess);
            setSaveState('saved');
            finishAndClose(currentRoutine.fromCalendar);
            showNotification('Entrenamiento guardado', 'success');
          } catch (error) {
            console.error('Error guardando entrenamiento:', error);
            setSaveState('idle');
            showNotification('Error al guardar el entrenamiento', 'error');
          }
        }
      );
      return;
    }

    if (!navigator.onLine) {
      if (selectedRoutine.isEditing) {
        showNotification('No se puede editar sin conexión', 'error');
        return;
      }

      const missingCatalog = selectedRoutine.exercises.some((exercise) => !exercise.exercise_id);
      if (missingCatalog) {
        showNotification('Conectate para guardar', 'error');
        return;
      }

      setSaveState('saving');
      try {
        await enqueue(selectedRoutine, confirmedSets);
        finishAndClose(selectedRoutine.fromCalendar);
        showNotification('Guardado sin conexión. Se sincroniza al reconectar.', 'success');
      } catch (error) {
        console.error('Error guardando entrenamiento offline:', error);
        showNotification('Error al guardar el entrenamiento offline', 'error');
        setSaveState('idle');
        return;
      }
      setSaveState('idle');
      return;
    }

    setSaveState('saving');
    try {
      await persistWorkout(selectedRoutine, confirmedSets, onSuccess);
      setSaveState('saved');
      finishAndClose(selectedRoutine.fromCalendar);
      showNotification('Entrenamiento guardado', 'success');
    } catch (error) {
      console.error('Error guardando entrenamiento:', error);
      setSaveState('idle');
      showNotification('Error al guardar el entrenamiento', 'error');
    }
  };

  const deleteWorkout = async (id: string, isDemoId: (id: string) => boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDemoId(id)) return;
    const workout = visibleWorkouts.find(w => w.id === id);
    if (workout?.syncStatus) {
      showNotification(workout.syncStatus === 'failed' ? 'No se pudo sincronizar este entrenamiento' : 'Esperá a que se sincronice', 'error');
      return;
    }
    const dateText = workout?.date ? ` del ${parseLocalDate(workout.date).toLocaleDateString('es-ES')}` : '';

    showConfirm(`¿Eliminar el entrenamiento "${workout?.routine_name || 'este'}"${dateText}?`, async () => {
      try {
        await supabase.from('workout_exercises').delete().eq('workout_id', id);
        const { error } = await supabase.from('workouts').delete().eq('id', id);
        if (error) throw error;
        setWorkouts(prev => prev.filter(w => w.id !== id));
        setSelectedWorkoutDetail(null);
        showNotification('Entrenamiento eliminado', 'success');
      } catch (error) {
        console.error('Error eliminando entrenamiento:', error);
        showNotification('Error al eliminar el entrenamiento', 'error');
      }
    });
  };

  const editWorkout = (workout: Workout, e: React.MouseEvent, fromCalendar = false) => {
    e.stopPropagation();
    if (workout.syncStatus) {
      showNotification(workout.syncStatus === 'failed' ? 'No se pudo sincronizar este entrenamiento' : 'Esperá a que se sincronice', 'error');
      return;
    }
    const isDemo = isDemoId(workout.id);
    const performedSets = new Set<string>();
    workout.exercises.forEach((ex, ei) =>
      ex.sets.forEach((set, si) => {
        if ((set.weight ?? 0) > 0 || (set.reps ?? 0) > 0) {
          performedSets.add(`${ei}-${si}`);
        }
      })
    );
    setConfirmedSets(performedSets);
    setActiveExerciseIndex(0);
    setSaveState('idle');
    setSelectedRoutine({
      ...workout,
      isEditing: !isDemo,
      isDemo: isDemo,
      fromCalendar,
      name: workout.routine_name,
      routine_id: workout.routine_id,
      exercises: workout.exercises.map(ex => ({ ...ex, sets: ex.sets.map(s => ({ ...s })) })),
    });
    setSelectedWorkoutDetail(null);
    setActiveTab('workout');
  };

  const toggleConfirmedSet = (exerciseIndex: number, setIndex: number) => {
    setConfirmedSets(prev => {
      const next = new Set(prev);
      const key = `${exerciseIndex}-${setIndex}`;
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const cancelWorkout = useCallback(() => {
    setSelectedRoutine(null);
    setConfirmedSets(new Set());
    setActiveExerciseIndex(0);
    setSaveState('idle');
  }, []);

  return {
    workouts,
    setWorkouts,
    workoutsLoading,
    selectedRoutine,
    setSelectedRoutine,
    confirmedSets,
    setConfirmedSets,
    activeExerciseIndex,
    setActiveExerciseIndex,
    saveState,
    loadWorkouts,
    persistQueuedWorkout,
    startWorkout,
    updateWorkoutSet,
    addWorkoutSet,
    removeWorkoutSet,
    saveWorkout,
    deleteWorkout,
    editWorkout,
    toggleConfirmedSet,
    cancelWorkout,
  };
}
